import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Briefcase, GraduationCap, ArrowRight, Sparkles } from "lucide-react";
import { Container, Section, PrimaryButton } from "../styles/GlobalStyles";

import InternshipImg from "../assets/internship-img.jpeg";
import CareerPathImage from "../assets/Career-path.jpeg";

const CareersSection = styled(Section)`
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  padding: 7rem 0;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 4.5rem 0;
  }

  &::before {
    content: '';
    position: absolute;
    top: -10%;
    left: -5%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 60%);
    border-radius: 50%;
    pointer-events: none;
  }
`;

const Header = styled.div`
  text-align: center;
  max-width: 760px;
  margin: 0 auto 4rem;
  position: relative;
  z-index: 2;

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.45rem 1rem;
    background: rgba(37, 99, 235, 0.08);
    color: var(--primary-700);
    border: 1px solid rgba(37, 99, 235, 0.15);
    border-radius: 99px;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 1.25rem;
  }

  h1 {
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.03em;
    line-height: 1.1;
    margin-bottom: 1rem;

    .gradient {
      background: linear-gradient(135deg, var(--primary-600), #8b5cf6);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  p {
    font-size: 1.1rem;
    color: #64748b;
    line-height: 1.7;
    max-width: 620px;
    margin: 0 auto;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  position: relative;
  z-index: 2;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const PathCard = styled(motion.div)`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 28px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: all 0.35s ease;
  box-shadow: 0 4px 12px -4px rgba(15, 23, 42, 0.05);

  &:hover {
    transform: translateY(-8px);
    border-color: var(--primary-300);
    box-shadow: 0 28px 56px -24px rgba(15, 23, 42, 0.22);
  }

  .media {
    position: relative;
    aspect-ratio: 16 / 9;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.6s ease;
    }

    .tag {
      position: absolute;
      top: 1rem;
      left: 1rem;
      display: inline-flex;
      align-items: center;
      gap: 0.45rem;
      background: rgba(15, 23, 42, 0.75);
      color: white;
      padding: 0.4rem 0.85rem;
      border-radius: 99px;
      font-size: 0.8rem;
      font-weight: 600;
      backdrop-filter: blur(10px);
    }
  }

  &:hover .media img {
    transform: scale(1.04);
  }

  .body {
    padding: 2rem;
    display: flex;
    flex-direction: column;
    flex: 1;

    h2 {
      font-size: 1.5rem;
      font-weight: 800;
      color: #0f172a;
      margin: 0 0 0.75rem;
      letter-spacing: -0.01em;
    }

    p {
      font-size: 1rem;
      color: #475569;
      line-height: 1.65;
      margin: 0 0 1.75rem;
      flex: 1;
    }
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.75rem;
  display: grid;
  gap: 0.6rem;

  li {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    color: #334155;
    font-size: 0.95rem;

    .tick {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--primary-600);
      flex-shrink: 0;
    }
  }
`;

const paths = [
  {
    icon: Briefcase,
    tag: "Hiring Now",
    title: "Internships & Entry Roles",
    description:
      "Work shoulder-to-shoulder with our engineering team on real client projects across AI, IoT and full-stack product work. Paid, outcome-focused, and built to turn learners into hireable engineers.",
    image: InternshipImg,
    features: [
      "Real production codebases, not mock tasks",
      "Senior engineer mentorship every week",
      "Performance-based conversion to full-time"
    ],
    cta: "See Open Roles",
    path: "/careerpage"
  },
  {
    icon: GraduationCap,
    tag: "Structured Programs",
    title: "Career Paths & Training",
    description:
      "Multi-month programs designed by our engineering team — Web Dev + DSA, AI/ML, IoT and two-to-three-year career tracks. Built for people serious about a long-term tech career.",
    image: CareerPathImage,
    features: [
      "Curriculum shaped by live project requirements",
      "1:1 reviews, system design, interview prep",
      "Portfolio projects that ship to real users"
    ],
    cta: "Explore Career Paths",
    path: "/career-paths"
  }
];

const Career = () => {
  const navigate = useNavigate();

  return (
    <CareersSection>
      <Container>
        <Header>
          <span className="eyebrow">
            <Sparkles size={14} /> Build Your Career With Willovate
          </span>
          <h1>
            Learn alongside engineers <br />
            <span className="gradient">shipping real products.</span>
          </h1>
          <p>
            Willovate is a product engineering studio first — and we grow the talent we work with. Whether you're looking for a first role or a structured multi-year path into tech, you'll learn by doing the work, not watching slides.
          </p>
        </Header>

        <Grid>
          {paths.map((p, i) => {
            const Icon = p.icon;
            return (
              <PathCard
                key={p.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="media">
                  <img src={p.image} alt={p.title} />
                  <span className="tag">
                    <Icon size={14} /> {p.tag}
                  </span>
                </div>
                <div className="body">
                  <h2>{p.title}</h2>
                  <p>{p.description}</p>
                  <FeatureList>
                    {p.features.map((f) => (
                      <li key={f}>
                        <span className="tick" />
                        {f}
                      </li>
                    ))}
                  </FeatureList>
                  <PrimaryButton
                    onClick={() => navigate(p.path)}
                    style={{ alignSelf: "flex-start" }}
                  >
                    {p.cta} <ArrowRight size={18} />
                  </PrimaryButton>
                </div>
              </PathCard>
            );
          })}
        </Grid>
      </Container>
    </CareersSection>
  );
};

export default Career;
